/// <reference lib="dom" />
/**
 * audioGraph.ts
 * Web Audio mixing graph — an `AudioContext` with one `GainNode` per bus.
 *
 * Sources are `HTMLAudioElement`s routed in via `createMediaElementSource`.
 * That is deliberate: ADR-011 specifies the Web Audio API, but the usual
 * `decodeAudioData` path needs `fetch`/`XMLHttpRequest` to read the file, and
 * both are banned by the offline-first ESLint rule (see ADR-005). Element
 * sources load a bundler-emitted URL exactly the way `imageLoader.ts` loads a
 * sprite with `new Image()`, so we get real gain buses with no network API.
 *
 * @module audioGraph
 */

import Logger from '../utils/logger';
import type { AudioBus } from '../types/audio';
import { DEFAULT_MUSIC_VOLUME, DEFAULT_SFX_VOLUME } from '../../constants/audio';

/** The live mixing graph. Only exists once audio has been unlocked. */
interface AudioGraph {
  ctx: AudioContext;
  music: GainNode;
  sfx: GainNode;
}

let _graph: AudioGraph | null = null;
let _unsupported = false;

/** Bumped every time a graph is built, so routing records from a torn-down
 * graph can never be mistaken for a live route. */
let _graphGeneration = 0;

// DECISION: WeakMap keyed on the element, valued by the generation of the graph
// that routed it. createMediaElementSource throws if called twice for the same
// element, so we must remember; storing the generation rather than a bare
// membership flag means a rebuilt graph invalidates stale records structurally
// instead of depending on teardown remembering to clear them. Weak so the
// element is GC'd with its entry rather than pinning every voice we created,
// and the value is a number so no dead AudioGraph is pinned either.
const _routed = new WeakMap<HTMLMediaElement, number>();

const _volumes: Record<AudioBus, number> = {
  music: DEFAULT_MUSIC_VOLUME,
  sfx: DEFAULT_SFX_VOLUME,
};

/** Clamp a requested volume into the 0–1 range the gain nodes expect. */
function _clamp(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.min(1, Math.max(0, v));
}

/**
 * Create the mixing graph if it does not exist yet.
 *
 * Call this only from a user-gesture handler. Browsers start an `AudioContext`
 * in the `suspended` state until a gesture resumes it, and a routed element
 * whose graph is suspended plays **silently** — worse than not routing at all.
 * Building the graph at unlock time means anything routed afterwards is
 * guaranteed to have a running destination.
 *
 * @returns The graph, or null when Web Audio is unavailable.
 */
export function ensureAudioGraph(): AudioGraph | null {
  if (_graph !== null) return _graph;
  if (_unsupported) return null;

  const Ctor = globalThis.AudioContext;
  if (typeof Ctor !== 'function') {
    _unsupported = true;
    Logger.warn('AudioGraph', 'Web Audio unavailable — using element volume');
    return null;
  }

  try {
    const ctx = new Ctor();
    const music = ctx.createGain();
    const sfx = ctx.createGain();
    music.gain.value = _volumes.music;
    sfx.gain.value = _volumes.sfx;
    music.connect(ctx.destination);
    sfx.connect(ctx.destination);
    _graph = { ctx, music, sfx };
    _graphGeneration += 1;
    Logger.debug('AudioGraph', 'Mixing graph created');
    return _graph;
  } catch (err) {
    _unsupported = true;
    Logger.warn('AudioGraph', 'Failed to create AudioContext', err);
    return null;
  }
}

/**
 * Resume the context if the browser suspended it. Safe to call repeatedly.
 * Never blocks — the returned promise is observed only for logging.
 */
export function resumeAudioGraph(): void {
  if (_graph === null) return;
  if (_graph.ctx.state !== 'suspended') return;
  try {
    const p: unknown = _graph.ctx.resume();
    if (p instanceof Promise) {
      p.catch((err: unknown) => Logger.debug('AudioGraph', 'Resume rejected', err));
    }
  } catch (err) {
    Logger.debug('AudioGraph', 'Resume threw', err);
  }
}

/**
 * Route a media element through the given bus.
 *
 * Deliberately does **not** create the graph — routing into a graph that has
 * not been unlocked would mute the element. Callers must fall back to
 * `element.volume` when this returns false.
 *
 * An element already routed into an *earlier* graph reports false rather than
 * true: `createMediaElementSource` refuses a second node for the same element,
 * so it genuinely cannot reach the current graph. That report is honesty, not
 * recovery — such an element stays captured by the dead graph and cannot be
 * made audible again by the element-volume fallback. Only the test-only reset
 * can produce this state; the shipped app never rebuilds the graph.
 *
 * @param el Element to route.
 * @param bus Destination bus.
 * @returns True when the element is connected to a live bus.
 */
export function routeElement(el: HTMLMediaElement, bus: AudioBus): boolean {
  if (_graph === null) return false;
  if (_routed.get(el) === _graphGeneration) return true;
  try {
    const source = _graph.ctx.createMediaElementSource(el);
    source.connect(bus === 'music' ? _graph.music : _graph.sfx);
    _routed.set(el, _graphGeneration);
    return true;
  } catch (err) {
    Logger.warn('AudioGraph', 'Failed to route element', err);
    return false;
  }
}

/**
 * Set a bus volume. Persists even when the graph does not exist yet, so a
 * volume chosen before unlock is applied when the graph is built.
 *
 * @param bus Bus to change.
 * @param value Volume in the 0–1 range; out-of-range input is clamped.
 */
export function setBusVolume(bus: AudioBus, value: number): void {
  const v = _clamp(value);
  _volumes[bus] = v;
  if (_graph === null) return;
  const node = bus === 'music' ? _graph.music : _graph.sfx;
  node.gain.value = v;
}

/**
 * Current volume for a bus (0–1).
 *
 * @param bus Bus to read.
 */
export function getBusVolume(bus: AudioBus): number {
  return _volumes[bus];
}

/** True when a live mixing graph exists and can carry routed elements. */
export function isGraphActive(): boolean {
  return _graph !== null;
}

/**
 * Tear down the graph and restore default volumes. Test-only helper — the
 * shipped app builds the graph once and keeps it for the session.
 *
 * Routing records are not cleared explicitly: the next `ensureAudioGraph()`
 * bumps the graph generation, which invalidates every prior record on its own.
 */
export function _resetAudioGraphForTesting(): void {
  if (_graph !== null) {
    try {
      void _graph.ctx.close();
    } catch {
      // Closing a context that never started is not an error worth logging.
    }
  }
  _graph = null;
  _unsupported = false;
  _volumes.music = DEFAULT_MUSIC_VOLUME;
  _volumes.sfx = DEFAULT_SFX_VOLUME;
}
